import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { analytics } from "@heycatch/sdk";
import { googleCallback } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [err, setErr] = useState("");
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = location.hash || window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sid = match ? decodeURIComponent(match[1]) : "";
    if (!sid) {
      setErr("Missing session. Please try signing in again.");
      return;
    }
    (async () => {
      try {
        const ref = (typeof window !== "undefined" && localStorage.getItem("lectura_ref")) || "";
        const user = await googleCallback(sid, ref);
        if (ref) { try { localStorage.removeItem("lectura_ref"); } catch { /* noop */ } }
        setUser(user);
        // Fire the HeyCatch conversion event so the Google-OAuth funnel isn't blind.
        // is_new_user comes back from googleCallback and lets us split "registered" vs "logged_in".
        try {
          if (user?.id) analytics.identify?.(user.id, {
            email: user.email, plan: user.plan || "free", role: user.role || "user",
          });
          analytics.track?.(user?.is_new_user ? "registered" : "logged_in", {
            method: "google_oauth",
            plan: user?.plan || "free",
          });
        } catch { /* HeyCatch offline */ }
        // Clear the hash before navigation so refresh doesn't re-trigger callback
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true, state: { user } });
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not complete sign-in. Please try again.");
      }
    })();
  }, [location.hash, setUser, navigate]);

  return (
    <div className="grain min-h-screen grid place-items-center px-4 relative z-10">
      <div className="max-w-sm w-full text-center rounded-3xl border border-border bg-card p-10">
        {err ? (
          <>
            <p className="font-serif text-2xl font-bold text-destructive">Sign-in failed</p>
            <p className="mt-2 text-sm text-muted-foreground">{err}</p>
            <button
              data-testid="auth-callback-retry"
              onClick={() => navigate("/login", { replace: true })}
              className="mt-6 rounded-full bg-primary text-primary-foreground px-6 py-2.5 font-semibold hover:opacity-90 transition"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-10 w-10 mx-auto animate-spin text-accent" />
            <p className="mt-6 font-serif text-xl">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
};
